import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {InitialOptionsForProductionOrder} from '@domain/production-schedule/initial-options-for-production-order';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {PlaceholderCategory} from '@domain/textile-data/finishing-and-finishing-template/placeholder-category';
import {ProductionOrders} from '@infrastructure/http/production-order/production-orders';
import {OverviewListProductionSchedule} from '@presentation/pages/texfab/production-schedule/overview/overview-list-production-schedule';
import {ReasonForFailureContext} from '@presentation/pages/texfab/production-schedule/overview/reason-for-failure-context';
import {GridModel, UnavailableArchiveError, UnhandledBackendError} from '@vdw/angular-component-library';
import {isEqual, map} from 'lodash-es';
import {Observable} from 'rxjs';
import {catchError, map as rxjsMap} from 'rxjs/operators';
import {getColoredYarnSetPaths} from '../helper/get-colored-yarn-sets-path';

@Injectable()
export class HttpProductionOrdersService implements ProductionOrders {
  private httpClient: HttpClient;
  private backendConfiguration: BackendConfiguration;

  public constructor(httpClient: HttpClient, backendConfiguration: BackendConfiguration) {
    this.httpClient = httpClient;
    this.backendConfiguration = backendConfiguration;
  }

  public getInitialOptionsForMachine(machineId: number): Observable<InitialOptionsForProductionOrder> {
    return this.httpClient
      .get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/initial-options?machine=${machineId}`)
      .pipe(rxjsMap((initialOptionsForMachineAndMachineQualityJSON: any) => InitialOptionsForProductionOrder.fromJSON(initialOptionsForMachineAndMachineQualityJSON)));
  }

  public getInitialOptionsForMachineAndMachineQuality(machineId: number, machineQualityId: number, reedDensity: number): Observable<InitialOptionsForProductionOrder> {
    return this.httpClient
      .get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/initial-options?machine-quality=${machineQualityId}&machine=${machineId}&reed-density=${reedDensity}`)
      .pipe(rxjsMap((initialOptionsForMachineAndMachineQualityJSON: any) => InitialOptionsForProductionOrder.fromJSON(initialOptionsForMachineAndMachineQualityJSON)));
  }

  public getCategoriesWithComponentsForProductionOrderLabel(): Observable<PlaceholderCategory[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/categories-with-components-for-production-order-label`)
      .pipe(rxjsMap((placeholderCategoriesJSON: any) => map(placeholderCategoriesJSON, (placeholderCategoryJSON: any) => PlaceholderCategory.fromJSON(placeholderCategoryJSON))));
  }

  public getCategoriesWithComponentsForPathLabel(): Observable<PlaceholderCategory[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/categories-with-components-for-path-label`)
      .pipe(rxjsMap((placeholderCategoriesJSON: any) => map(placeholderCategoriesJSON, (placeholderCategoryJSON: any) => PlaceholderCategory.fromJSON(placeholderCategoryJSON))));
  }

  public getCategoriesWithComponentsForProductionOrderName(): Observable<PlaceholderCategory[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/categories-with-components-for-production-order-name`)
      .pipe(rxjsMap((placeholderCategoriesJSON: any) => map(placeholderCategoriesJSON, (placeholderCategoryJSON: any) => PlaceholderCategory.fromJSON(placeholderCategoryJSON))));
  }

  public delete(id: number | string): Observable<void> {
    return undefined;
  }

  public getAll(): Observable<OverviewListProductionSchedule[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules`)
      .pipe(rxjsMap((productionOrdersJSON: any) => map(productionOrdersJSON, (productionOrderJSON: any) => OverviewListProductionSchedule.fromJSON(productionOrderJSON))));
  }

  public getById(id: number): Observable<ProductionSchedule> {
    return undefined;
  }

  public save(item: ProductionSchedule): Observable<number> {
    return undefined;
  }

  public update(item: ProductionSchedule): Observable<void> {
    return undefined;
  }

  public updateProductionOrder(
    productionScheduleId: string,
    productionSchedulePathsOfColoredYarnSets: ProductionSchedulePathsOfColoredYarnSet[],
    factorForConversionFromMillimetersToDents: number,
    productionScheduleStatus: ProductionScheduleStatus
  ): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionScheduleId}`, {
      status: ProductionScheduleStatus[productionScheduleStatus],
      coloredYarnSetPaths: getColoredYarnSetPaths(productionSchedulePathsOfColoredYarnSets, factorForConversionFromMillimetersToDents)
    });
  }

  public getWeavingOrderXMLForProductionOrder(productionOrderId: number): Observable<string> {
    return this.httpClient.get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionOrderId}/weaving-order/xml`, {responseType: 'text'});
  }

  public getWeavingOrderXSLTForProductionOrder(productionOrderId: number): Observable<string> {
    return this.httpClient.get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionOrderId}/weaving-order/xslt`, {responseType: 'text'});
  }

  public getReasonForFailure(productionOrderId: number): Observable<ReasonForFailureContext> {
    return this.httpClient.get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionOrderId}/failure-reason`).pipe(
      rxjsMap((response: ReasonForFailureContext) => {
        return response;
      })
    );
  }

  public downloadEpFiles(productionOrderId: string): Observable<ArrayBuffer> {
    return this.httpClient.get(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionOrderId}/download-eps`, {responseType: 'arraybuffer'}).pipe(
      catchError((error: UnhandledBackendError) => {
        if (isEqual(error.errorContext?.status, 412)) {
          throw new UnavailableArchiveError();
        } else {
          throw error;
        }
      })
    );
  }

  public removeMachineFiles(productionOrderId: string | number, machineFromProductionOrderId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getAggregatorEndpoint()}productionorders/${productionOrderId}/files-on-machine?machineId=${machineFromProductionOrderId}`);
  }

  public getSlice(gridModel: GridModel): Observable<OverviewListProductionSchedule[]> {
    const url = `${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/get`;

    return this.httpClient.post(url, gridModel.toJSON()).pipe(
      rxjsMap((productionOrdersJSON: any) => {
        return map(productionOrdersJSON, (productionOrder: any) => {
          return OverviewListProductionSchedule.fromJSON(productionOrder);
        });
      })
    );
  }

  public getPossibleValues(columnIdentifier: string): Observable<string[] | number[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${columnIdentifier}/possible-values`)
      .pipe(rxjsMap((response: string[] | number[]) => response));
  }

  public checkCompatibilityWithMachine(productionOrderId: number, machineId: number): Observable<number> {
    return this.httpClient.get<number>(`${this.backendConfiguration.getAggregatorEndpoint()}productionorders/${productionOrderId}/is-compatible-machine/${machineId}`);
  }

  public completeProductionOrder(productionOrderId: number | string, schedule: ProductionSchedule | ProductionScheduleStatus): Observable<void> {
    if (schedule instanceof ProductionSchedule) {
      return this.httpClient.put<void>(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionOrderId}`, schedule.toJSON(true));
    } else {
      const status = schedule as ProductionScheduleStatus;
      return this.httpClient.put<void>(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionOrderId}`, {status});
    }
  }

  public getReadyToQueue(productionOrderId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this.backendConfiguration.getProductionOrderEndpoint()}productionorders/${productionOrderId}/ready-to-queue`);
  }
}
